/**
 * A canvas of nodes and edges, drawn as finixt-ui's: a hairline card on the
 * canvas surface with a dotted grid (Feature 71). The page owns the nodes and
 * edges and decides what every gesture does; the canvas only reports them.
 * Deleting is never a key press here: a step or a link goes from its
 * inspector, where the operator sees what goes with it.
 */
import {
  Background,
  BackgroundVariant,
  ReactFlow,
  type Connection,
  type Edge,
  type EdgeMouseHandler,
  type EdgeTypes,
  type Node,
  type NodeChange,
  type NodeMouseHandler,
  type NodeTypes,
} from '@xyflow/react';

export interface FlowCanvasProps<N extends Node, E extends Edge> {
  nodes: N[];
  edges: E[];
  nodeTypes: NodeTypes;
  edgeTypes: EdgeTypes;
  /** Pixels; the canvas needs a height of its own. */
  height: number;
  onNodesChange: (changes: NodeChange<N>[]) => void;
  onConnect: (connection: Connection) => void;
  isValidConnection: (connection: Connection | E) => boolean;
  onNodeClick: NodeMouseHandler<N>;
  onEdgeClick: EdgeMouseHandler<E>;
  onPaneClick: () => void;
  /** Read-only: no dragging, no connecting (a run's canvas). */
  readOnly?: boolean | undefined;
  /** The pointer coming to rest on a node or a link, and leaving it; a drag or a pan starting. */
  onNodeMouseEnter?: NodeMouseHandler<N> | undefined;
  onNodeMouseLeave?: NodeMouseHandler<N> | undefined;
  onEdgeMouseEnter?: EdgeMouseHandler<E> | undefined;
  onEdgeMouseLeave?: EdgeMouseHandler<E> | undefined;
  onNodeDragStart?: (() => void) | undefined;
  onMoveStart?: (() => void) | undefined;
}

export function FlowCanvas<N extends Node, E extends Edge>({
  nodes,
  edges,
  nodeTypes,
  edgeTypes,
  height,
  onNodesChange,
  onConnect,
  isValidConnection,
  onNodeClick,
  onEdgeClick,
  onPaneClick,
  readOnly = false,
  onNodeMouseEnter,
  onNodeMouseLeave,
  onEdgeMouseEnter,
  onEdgeMouseLeave,
  onNodeDragStart,
  onMoveStart,
}: FlowCanvasProps<N, E>) {
  return (
    <div
      style={{
        height,
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface-canvas)',
        overflow: 'hidden',
      }}
    >
      <ReactFlow<N, E>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        // Only the handlers given: the library's props admit no explicit undefined.
        {...(onNodeMouseEnter ? { onNodeMouseEnter } : {})}
        {...(onNodeMouseLeave ? { onNodeMouseLeave } : {})}
        {...(onEdgeMouseEnter ? { onEdgeMouseEnter } : {})}
        {...(onEdgeMouseLeave ? { onEdgeMouseLeave } : {})}
        {...(onNodeDragStart ? { onNodeDragStart } : {})}
        {...(onMoveStart ? { onMoveStart } : {})}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable
        deleteKeyCode={null}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        minZoom={0.3}
        maxZoom={1.5}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={16}
          size={1}
          color="var(--border-input)"
        />
      </ReactFlow>
    </div>
  );
}
